"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useForm = void 0;
const react_1 = require("react");
const fieldTypes_1 = require("./fieldTypes");
const useActions_1 = require("./useActions");
const validate_1 = require("./validate");
/**
 * Store various state related to form processing.
 *
 * Use it in a form component that can edit data in one or more input fields:
 *
 * ```typescript
 * import {useForm} from "@use-ui/hooks"
 * import {Input} from "@use-ui/bootstrap3"
 *
 * interface LoginData {
 *   login: string
 *   password: string
 * }
 *
 * const {fields, progress, action, error} = useForm<LoginData>()
 *
 * const login = action(async ({login, password}: LoginData) => {
 *   await services.auth.login({login, password})
 * })

 * return (
 *   <div>
 *     <Input label="Login" field={fields.login} required autoFocus disabled={progress} />
 *     <Input label="Password" field={fields.password} required autoFocus disabled={progress} />

 *     <Button onClick={login} progress={progress}>
 *       Sign In
 *     </Button>
 *   </div>
 * )
 * ```
 *
 * Form consists of multiple {@link Field}s. Each field has a value and a validation error. Each field is linked to a
 * {@link FieldElement}. {@link FieldElement} is implemented by input component (Input in an example above), and contains information about field's {@link FieldType}
 * and validation {@link Constraint}s.
 *
 * Field value is a string. Form data can be of different types, and {@link FieldType} defines how to convert form data to a field value and back.
 * There are four predefined {@link FieldTypeName}s. Custom field types can also be used.
 *
 * Input components are implemented in a separate libraries, for example [@use-ui/bootstrap3](https://github.com/vasyas/use-ui-bootstrap3).
 *
 * In addition, form creates a set of action, see {@link useActions}. Form action behaves in the same way as in `useActions`, except:
 * 1) Action implementation receives current form data as the first param
 * 2) Actions are not launched if form is invalid.
 *
 * To set initial data for the form fields, pass data as object:
 * ```
 * const form = useForm<LoginData>({login: "my-login"})
 * ```
 *
 * or use async loader function:
 * ```
 * const form = useForm<LoginData>(async () => await fetchInitialData(params))
 * ```
 *
 * @typeParam Data Type of form's data. Form data items can be of any type. Type should be convertible to a string using {@link FieldType} specified via input component.
 * @param dataInitializer  initial data for field values. Either object or async loader functions
 */
function useForm(dataInitializer) {
    const [initialData, setInitialData] = (0, react_1.useState)(typeof dataInitializer == "function" ? undefined : dataInitializer);
    const [errors, setErrors] = (0, react_1.useState)({});
    const [values, setValues] = (0, react_1.useState)({});
    const fieldElements = (0, react_1.useRef)({});
    const action = (0, useActions_1.useActions)();
    (0, react_1.useEffect)(() => {
        if (typeof dataInitializer == "function") {
            if (initialData === undefined) {
                dataInitializer().then(setInitialData);
            }
        }
        else {
            setInitialData(dataInitializer);
        }
    }, [JSON.stringify(dataInitializer)]);
    function getActionData() {
        const r = initialData ? { ...initialData } : {};
        Object.keys(fieldElements.current).forEach((name) => {
            let d;
            if (typeof values[name] != "undefined") {
                d = getConfiguredFieldType(name).valueToData(values[name]);
            }
            else {
                d = initialData?.[name];
            }
            r[name] = d;
        });
        return data;
    }
    function formAction(impl, options = { validate: true }) {
        return action.action(async (p) => {
            if (options.validate && revalidate()) {
                return;
            }
            try {
                await impl(getActionData(), p);
            }
            catch (e) {
                if (e.fieldErrors) {
                    setErrors({
                        ...errors,
                        ...e.fieldErrors,
                    });
                }
                throw e;
            }
        });
    }
    function getFieldValue(name, currentValues = values) {
        const currentValue = currentValues[name];
        if (currentValue !== undefined) {
            return currentValue;
        }
        if (initialData) {
            return getConfiguredFieldType(name).dataToValue(initialData[name]);
        }
        return "";
    }
    function getConfiguredFieldType(fieldName) {
        const type = fieldElements.current[fieldName]?.type ?? "string";
        return typeof type == "string" ? (0, fieldTypes_1.getFieldType)(type) : type;
    }
    function createField(name) {
        return {
            getFieldElement() {
                return fieldElements.current[name];
            },
            setFieldElement(fieldElement) {
                fieldElements.current[name] = fieldElement;
            },
            getValue() {
                return getFieldValue(name);
            },
            setValue(s) {
                setValues((values) => ({ ...values, [name]: s }));
                // TODO useEffect instead of this, b/c values maybe old?
                const newValues = {
                    ...values,
                    [name]: s,
                };
                if (errors[name])
                    updateValidationError(name, newValues);
            },
            getError() {
                return errors[name];
            },
            onBlur() {
                const fixed = autofixValue(name);
                const revalidate = () => {
                    if (!disableBlurValidation)
                        updateValidationError(name);
                };
                if (fixed) {
                    setTimeout(revalidate);
                }
                else {
                    revalidate();
                }
            },
            onFocus() { },
        };
    }
    function autofixValue(name) {
        /*
        const value = this.getValue()
        const {name} = this.props
    
        if (typeof value == "string") {
          const trimmed = value.trim()
    
          if (trimmed != value) {
            const data = this.getDataType().toData(trimmed)
            this.context.form.updateInput(name, trimmed, data)
            return true
          }
        }
         */
        return false;
    }
    function updateValidationError(name, currentValues = values) {
        const error = validate(name, currentValues);
        setErrors({
            ...errors,
            [name]: error,
        });
    }
    function validate(name, currentValues = values) {
        const fieldElement = fieldElements.current[name];
        if (!fieldElement)
            return;
        const value = getFieldValue(name, currentValues);
        return (0, validate_1.message)(fieldElement.constraint, value, validate_1.enValidateMessages);
    }
    function createFields() {
        return new Proxy({}, {
            get(target, name) {
                // skip internal props
                if (typeof name != "string")
                    return target[name];
                return createField(name);
            },
        });
    }
    function createData() {
        const r = initialData ? { ...initialData } : {};
        for (const name of Object.keys(values)) {
            r[name] = getConfiguredFieldType(name).valueToData(values[name]);
        }
        return r;
    }
    function revalidate() {
        const errors = {};
        let focused = false;
        Object.keys(fieldElements.current).forEach((name) => {
            errors[name] = validate(name);
            if (!focused && errors[name]) {
                fieldElements.current[name]?.focus();
                focused = true;
            }
        });
        setErrors(errors);
        return focused;
    }
    function updateValues(update) {
        const newValues = {
            ...values,
            ...update,
        };
        setValues(newValues);
        const updatedErrors = Object.keys(update).reduce((r, name) => {
            return {
                ...r,
                [name]: validate(name, newValues),
            };
        }, {});
        setErrors({
            ...errors,
            ...updatedErrors,
        });
    }
    // use ref for fields and data?
    const fields = createFields();
    const data = createData();
    // console.log("Form render", JSON.parse(JSON.stringify({data, values, errors})))
    return {
        fields,
        data,
        updateValues,
        error: action.error,
        progress: action.progress,
        action: formAction,
    };
}
exports.useForm = useForm;
// To disable validation on special occasions (ie selecting item on custom selects)
let disableBlurValidation = false;
